// Bookmark edits (Ben, Oct 13 2026): the player's own names for their map bookmarks and the order they keep them in, one
// book per world. The game stores a bookmark as the city's name + "x,y", newest first, and can only add and delete (it
// has no rename or reorder, and the game code is pull-only), so the Worker keeps this book in Cloudflare KV under the
// signed-in player (worker/marks.ts), next to the city tags. Only Valor Web reads it; the live app keeps its own list.
// This file is shared by the Worker and the app: no imports, no DOM.

export const MARK_NAME_MAX = 30;
const MAX_MARKS = 500;

export interface MarkBook {
  /** bookmark coords ("x,y") in the player's order */
  order: string[];
  /** coords → the player's own name for that bookmark */
  names: Record<string, string>;
}

export const emptyMarkBook = (): MarkBook => ({ order: [], names: {} });

/** "x,y" (spaces allowed) → "x,y", or "" when it isn't a pair of whole numbers. */
export const markCoords = (s: unknown): string => {
  if (typeof s !== "string") return "";
  const m = /^\s*(\d{1,5})\s*,\s*(\d{1,5})\s*$/.exec(s);
  return m ? `${Number(m[1])},${Number(m[2])}` : "";
};

/** A bookmark name as stored: trimmed, inner whitespace collapsed, no angle brackets, at most MARK_NAME_MAX characters. */
export const cleanMarkName = (s: unknown): string =>
  typeof s === "string" ? s.replace(/[<>]/g, "").replace(/\s+/g, " ").trim().slice(0, MARK_NAME_MAX).trim() : "";

/** Anything → a valid book: bad or repeated coords are dropped, empty names left out. Used on both sides of the wire. */
export function cleanMarkBook(x: unknown): MarkBook {
  const out = emptyMarkBook();
  if (!x || typeof x !== "object") return out;
  const raw = x as { order?: unknown; names?: unknown };
  if (Array.isArray(raw.order)) {
    const seen = new Set<string>();
    for (const c of raw.order) {
      if (out.order.length >= MAX_MARKS) break;
      const k = markCoords(c);
      if (k && !seen.has(k)) {
        seen.add(k);
        out.order.push(k);
      }
    }
  }
  if (raw.names && typeof raw.names === "object" && !Array.isArray(raw.names)) {
    let n = 0;
    for (const [c, name] of Object.entries(raw.names as Record<string, unknown>)) {
      if (n >= MAX_MARKS) break;
      const k = markCoords(c);
      const nm = cleanMarkName(name);
      if (k && nm && !(k in out.names)) {
        out.names[k] = nm;
        n++;
      }
    }
  }
  return out;
}

export interface Mark {
  coords: string;
  /** what the list shows: the player's name, else the city's */
  name: string;
  /** the city's name as the game has it */
  city: string;
  renamed: boolean;
}

/** The game's bookmarks (newest first) laid out by the book: ones the book doesn't know yet (new since the player last
 *  arranged the list) stay on top, newest first, as the game puts them; the rest follow in the player's order. */
export function applyMarkBook(list: { name: string; coords: string }[], book: MarkBook): Mark[] {
  const mk = (b: { name: string; coords: string }): Mark => {
    const own = book.names[markCoords(b.coords)];
    return { coords: b.coords, city: b.name, name: own || b.name, renamed: !!own && own !== b.name };
  };
  const rank = new Map(book.order.map((c, i) => [c, i]));
  const fresh = list.filter((b) => !rank.has(markCoords(b.coords)));
  const known = list.filter((b) => rank.has(markCoords(b.coords))).sort((a, b) => rank.get(markCoords(a.coords))! - rank.get(markCoords(b.coords))!);
  return [...fresh, ...known].map(mk);
}

/** The book for a list as the player left it: its order, and a name only where it differs from the city's. */
export function bookOf(list: { coords: string; city: string; name: string }[]): MarkBook {
  const book = emptyMarkBook();
  for (const m of list) {
    const k = markCoords(m.coords);
    if (!k || book.order.includes(k)) continue;
    book.order.push(k);
    const nm = cleanMarkName(m.name);
    if (nm && nm !== m.city) book.names[k] = nm;
  }
  return book;
}

/** Move one item of a list from one place to another (a new array). */
export function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= list.length) return list;
  const out = list.slice();
  const [it] = out.splice(from, 1);
  out.splice(Math.max(0, Math.min(out.length, to)), 0, it);
  return out;
}
